import React, { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, FlexRow, ISelectOption } from 'ui-toolkit';
import { NameControl } from './NameControl';
import { EditLenderClient } from './EditLenderClient';
import { ILenderClientSchema } from './LenderClientSchema';
import { PartySummaryDropdown } from 'components/order/overview/Summary/partiesTab/PartySummaryDropdown';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { IParty } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { isNil } from 'lodash';
import { mergeTests } from '../../../../../ui-toolkit/test-utilities/mergeTests';
import { labelComparator } from '../../../../../utilities/labelComparator';

interface ILenderClientProps {
  parties: IParty[];
  associatedLenders: { [id: string]: Array<ISelectOption<string>> };
  getAssociatedLenders: (clientId: string) => Promise<any>;
  allClients: Array<ISelectOption<string>>;
  allUsers: Array<ISelectOption<string>>;
  getLenderUsers: (lenderId: string) => Promise<any>;
}

const fieldsName = {
  [ePartyPurpose.lender]: 'lenderId',
  [ePartyPurpose.client]: 'clientId',
  [ePartyPurpose.order_contact]: 'orderContactId',
};

export const LenderClient: React.FC<ILenderClientProps> = ({
  parties,
  associatedLenders,
  getAssociatedLenders,
  allClients,
  allUsers,
  getLenderUsers,
}) => {
  const sortedClients = allClients.toSorted(labelComparator);
  const sortedUsers = allUsers.toSorted(labelComparator);

  const [editPartiesPurpose, setEditFieldPurpose] = useState<ePartyPurpose[]>([]);

  const getPartyPurposeById = useCallback(
    (id: string): ePartyPurpose | null => {
      return parties.find((party) => party.id === id)?.purpose ?? null;
    },
    [parties]
  );

  const formik = useFormikContext<ILenderClientSchema>();

  useEffect(() => {
    if (associatedLenders[formik.values.clientId] !== undefined) {
      const lenderIsAssociated = associatedLenders[formik.values.clientId].find(
        (lender) => lender.value === formik.values.lenderId
      );
      if (
        !lenderIsAssociated &&
        editPartiesPurpose.includes(ePartyPurpose.client) &&
        !editPartiesPurpose.includes(ePartyPurpose.lender)
      ) {
        // try to find matched lender (lender with the same name) if current lender is not associated to new client
        setEditFieldPurpose([ePartyPurpose.client, ePartyPurpose.lender, ePartyPurpose.order_contact]);
        const client = sortedClients.find((client) => client.value === formik.values.clientId);
        const matchedLender = associatedLenders[formik.values.clientId].find(
          (lender) => lender.label === client!.label
        );
        formik.setFieldTouched('lenderId');
        formik.setFieldValue('lenderId', matchedLender ? matchedLender.value : null);
        if (formik.values.orderContactId) {
          formik.setFieldTouched('orderContactId');
          formik.setFieldValue('orderContactId', null);
        }
        if (matchedLender) {
          getLenderUsers(matchedLender.value);
        }
      }
    }
  }, [associatedLenders, editPartiesPurpose, formik, sortedClients, getLenderUsers]);

  const onChange = useCallback(
    async (purpose: ePartyPurpose, value: string) => {
      await formik.setFieldTouched(fieldsName[purpose] as any);
      await formik.setFieldValue(fieldsName[purpose] as any, value);
      if (purpose === ePartyPurpose.client) {
        // get associated lenders for a new client
        if (isNil(associatedLenders[value])) {
          await getAssociatedLenders(value);
        }
      }
      if (purpose === ePartyPurpose.lender) {
        // user should change the order contact if lender was changed
        if (formik.values.orderContactId || editPartiesPurpose.includes(ePartyPurpose.order_contact)) {
          await formik.setFieldTouched('orderContactId');
          await formik.setFieldValue('orderContactId', null);
          await getLenderUsers(value);
          setEditFieldPurpose([...editPartiesPurpose, ePartyPurpose.order_contact]);
        }
      }
    },
    [formik, associatedLenders, getAssociatedLenders, getLenderUsers, editPartiesPurpose]
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        formik.handleSubmit(e);
        setEditFieldPurpose([]);
      } catch (e: any) {}
    },
    [formik]
  );

  return (
    <form onSubmit={onSubmit}>
      {parties.map((party) => (
        <PartySummaryDropdown
          key={party.id + '_' + party.purpose}
          label={DisplayPartyPurpose[party.purpose]}
          onClick={() => setEditFieldPurpose([...editPartiesPurpose, party.purpose])}
        >
          <FlexRow>
            {editPartiesPurpose.includes(party.purpose) ? (
              <EditLenderClient
                onChange={(value) => onChange(getPartyPurposeById(party.id)!, value)}
                fieldName={getPartyPurposeById(party.id) ? fieldsName[getPartyPurposeById(party.id)!] : ''}
                options={
                  party.purpose === ePartyPurpose.lender
                    ? (associatedLenders[formik.values.clientId]?.toSorted(labelComparator) ?? [])
                    : party.purpose === ePartyPurpose.order_contact
                      ? sortedUsers
                      : sortedClients
                }
              />
            ) : (
              <NameControl
                data-test-id={mergeTests(party.purpose, 'name')}
                party={party}
                showPartyType={false}
                intent="faded"
              />
            )}

            <div>
              {editPartiesPurpose.includes(party.purpose) && (
                <Button intent="ghost" isInline type="submit" disabled={!formik.isValid}>
                  Update
                </Button>
              )}
            </div>
          </FlexRow>
        </PartySummaryDropdown>
      ))}
    </form>
  );
};
